import React, { useMemo } from 'react'
import { ButtonType, CustomButton, InfoTooltip } from '../../../../../components'
import { CustomSelect } from '../../components/CustomSelect'
import { ViewBox } from '../../components/ViewBox'
import { Inputs } from '../../components/Inputs'
import { getResolutionDimensions, getResolutionLabel } from './convertDisplay'
import { Direction, Orientation, Resolution, ConnectionType } from './display.enums'
import styles from './DisplayBasicConfigUI.module.css'

const numberFields = ['gate_id']
const percentageFields = ['min_confidence_level', 'day_brightness', 'night_brightness']

const SECTION_KEYS = {
    GENERAL: 'generalSection',
    DETECTIONS: 'detectionSection',
    DISPLAY: 'displaySection',
    NETWORK: 'networkSection'
}

const createOptions = <T extends string>(enumType: Record<string, T>): { id: string; label: string }[] =>
    useMemo(
        () =>
            Object.values(enumType).map((val) => ({
                id: val as string,
                label: val as string
            })),
        [enumType]
    )

const handleInputChange = (key, value, onUpdate) => {
    let newValue = value
    if (numberFields.includes(key)) newValue = value === '' ? '' : Number(value)
    if (percentageFields.includes(key)) newValue = value / 100
    onUpdate(key, newValue)
}

const sectionData = (data) => ({
    general: [
        { name: 'Sibling Lot Default', value: data.lot_id, key: 'lot_id' }
        // { name: 'Lot IDs', value: data.lot_ids, key: 'lot_ids' }
    ],
    detections: [
        {
            name: 'Gate ID',
            value: data.gate_id,
            key: 'gate_id',
            dataType: 'number'
        },
        {
            name: 'Ignore Detections Below [%]',
            value: data.min_confidence_level ? data.min_confidence_level * 100 : '',
            key: 'min_confidence_level',
            dataType: 'number'
        }
    ],
    display: [
        {
            name: 'Day Brightness [%]',
            value: data.day_brightness ? data.day_brightness * 100 : '',
            key: 'day_brightness',
            dataType: 'number'
        },
        {
            name: 'Night Brightness [%]',
            value: data.night_brightness ? data.night_brightness * 100 : '',
            key: 'night_brightness',
            dataType: 'number'
        }
    ]
})

interface DisplayBasicConfigUIProps {
    data: any
    onUpdate: any
    onSave: () => void
    onForceUpdate: () => void
    setDefaultToBaseForSection: any
    disabled: boolean
    forceUpdateDisabled: boolean
    validationErrors: any
    avaiableLots: any[]
    loadingSave: boolean
    loadingForceUpdate: boolean
}

export const DisplayBasicConfigUI = ({
    data,
    onUpdate,
    onSave,
    onForceUpdate,
    setDefaultToBaseForSection,
    disabled,
    forceUpdateDisabled,
    validationErrors,
    avaiableLots,
    loadingSave,
    loadingForceUpdate
}: DisplayBasicConfigUIProps) => {
    const sections = sectionData(data)
    const connectionTypeOptions = useMemo(
        () =>
            Object.keys(ConnectionType).map((t) => ({
                id: t,
                label: t
            })),
        []
    )
    return (
        <div className={styles.root}>
            <div className={styles.actions}>
                <CustomButton
                    type={ButtonType.SAVE}
                    onClick={onSave}
                    disabled={disabled || loadingSave || loadingForceUpdate}
                    isLoading={loadingSave}
                >
                    Save Configuration
                </CustomButton>
                <InfoTooltip content={forceUpdateDisabled ? 'No changes to force update' : null}>
                    <CustomButton
                        type={ButtonType.DELETE}
                        onClick={onForceUpdate}
                        disabled={forceUpdateDisabled || loadingSave || loadingForceUpdate}
                        isLoading={loadingForceUpdate}
                    >
                        Force Update
                    </CustomButton>
                </InfoTooltip>
            </div>

            <div className={styles.columns}>
                <div className={styles.column}>
                    <ViewBox
                        onDefault={() => setDefaultToBaseForSection(SECTION_KEYS.GENERAL)}
                        title="General"
                        toggleLable="Test Mode"
                        checked={data.test_mode}
                        setChecked={() => onUpdate('test_mode', !data.test_mode)}
                        confirmText="Are you sure you want to enable testmode? Technical teams will not be alerted during this time in case of failures!"
                    >
                        <CustomSelect
                            label={sections.general[0].name}
                            options={[
                                { id: undefined, label: 'None' },
                                ...avaiableLots.map((lot) => ({ id: lot, label: lot }))
                            ]}
                            value={{
                                id: data.lot_id,
                                label: data.lot_id
                            }}
                            onChange={(selected) => {
                                if (selected) {
                                    handleInputChange('lot_id', selected.id, onUpdate)
                                }
                            }}
                        />
                    </ViewBox>
                    <ViewBox onDefault={() => setDefaultToBaseForSection(SECTION_KEYS.NETWORK)} title="Networking">
                        <CustomSelect
                            label="Connection Type"
                            options={connectionTypeOptions}
                            value={{
                                id: data.lot_mqtt_ip,
                                label:
                                    Object.keys(ConnectionType).find(
                                        (key) => ConnectionType[key as keyof typeof ConnectionType] === data.lot_mqtt_ip
                                    ) || data.lot_mqtt_ip
                            }}
                            onChange={(selected) => {
                                if (selected) {
                                    onUpdate('lot_mqtt_ip', ConnectionType[selected.id as keyof typeof ConnectionType])
                                }
                            }}
                        />
                    </ViewBox>
                </div>
                <div className={styles.column}>
                    <ViewBox
                        onDefault={() => setDefaultToBaseForSection(SECTION_KEYS.DETECTIONS)}
                        title="Detections"
                        toggleLable="Quick Events Enabled"
                        checked={!data.disable_gui_event_publish}
                        setChecked={() => onUpdate('disable_gui_event_publish', !data.disable_gui_event_publish)}
                    >
                        <Inputs
                            data={sections.detections}
                            onInputChange={(key, val) => handleInputChange(key, val, onUpdate)}
                            validationErrors={validationErrors}
                        />
                        <CustomSelect
                            label="Direction"
                            options={createOptions(Direction)}
                            value={{ id: data.direction, label: data.direction }}
                            onChange={(direction) => direction && onUpdate('direction', direction.id)}
                        />
                    </ViewBox>
                    <ViewBox onDefault={() => setDefaultToBaseForSection(SECTION_KEYS.DISPLAY)} title="Display">
                        <CustomSelect
                            label="Orientation"
                            options={createOptions(Orientation)}
                            value={{
                                id: data.monitor_orientation,
                                label: data.monitor_orientation
                            }}
                            onChange={(orientation) => orientation && onUpdate('monitor_orientation', orientation.id)}
                        />
                        <div className={styles.resolutionSelect}>
                            <CustomSelect
                                label="Resolution"
                                options={createOptions(Resolution)}
                                value={{
                                    id: getResolutionLabel(data.resolution_width, data.resolution_height),
                                    label: getResolutionLabel(data.resolution_width, data.resolution_height)
                                }}
                                onChange={(resolution) => {
                                    if (resolution) {
                                        const { width, height } = getResolutionDimensions(resolution.id as Resolution)
                                        onUpdate('resolution_width', width)
                                        onUpdate('resolution_height', height)
                                    }
                                }}
                            />
                        </div>
                        <Inputs
                            data={sections.display}
                            onInputChange={(key, val) => handleInputChange(key, val, onUpdate)}
                            validationErrors={validationErrors}
                        />
                    </ViewBox>
                </div>
            </div>
        </div>
    )
}
